// Physics v2 mirror — quarter section times (byte-parallel with
// racecore/telemetry_v2.py; cross-language parity pinned by
// core/tests/js_v2_parity.mjs).
//
// v2 = the v1 envelope (same draws, same finish times) PLUS one extra
// per-horse float from the NEW domain "horseracing-telemetry:v2" and
// per-horse quarter section times computed from an archetype-flavoured pace
// shape FITTED to the drawn order. All inputs are public (racecard
// archetypes + distance + revealed seed), so any client re-derives splits
// exactly.

import { StreamGenerator, deriveTelemetry } from './fairness.js';

export const PHYSICS_VERSION_V2 = 2;
export const TELEMETRY_DOMAIN_V2 = 'horseracing-telemetry:v2';

export const ARCHETYPE_MU = { sprinter: 800, miler: 1000, stayer: 1200 };
const FLAVOUR_GAIN = 0.06;
const PACE_LO = 0.86;
const PACE_HI = 1.14;
const QUARTERS = [0.25, 0.50, 0.75, 1.00];
const SHAPE_STEPS = 1024;

function bump(u, center, width) {
  const t = 1.0 - ((u - center) / width) ** 2;
  return t <= 0 ? 0 : t * t;
}

function pace(horse, u) {
  return 1.0
    + (horse.early - 1.0) * bump(u, 0.12, 0.40)
    + (horse.kick - 1.0) * bump(u, 1.0, 0.45);
}

function flavouredHorse(horse, archetype, distanceM, sectionSeed) {
  const mu = ARCHETYPE_MU[archetype ?? ''] ?? distanceM;
  const bias = Math.max(-1.0, Math.min(1.0, (distanceM - mu) / 400.0));
  const wobble = (sectionSeed * 2.0 - 1.0) * 0.01;
  const early = Math.min(PACE_HI, Math.max(PACE_LO, horse.early + FLAVOUR_GAIN * bias + wobble));
  const kick = Math.min(PACE_HI, Math.max(PACE_LO, horse.kick - FLAVOUR_GAIN * bias - wobble));
  return { ...horse, early, kick };
}

function sectionTimesMs(horse) {
  const cum = new Array(SHAPE_STEPS + 1).fill(0);
  for (let k = 0; k < SHAPE_STEPS; k++) {
    cum[k + 1] = cum[k] + pace(horse, (k + 0.5) / SHAPE_STEPS);
  }
  const total = cum[SHAPE_STEPS];
  const finishS = horse.finishTime;
  const times = [];
  for (const q of QUARTERS) {
    const target = q * total;
    let lo = 0;
    let hi = SHAPE_STEPS;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] < target) lo = mid + 1; else hi = mid;
    }
    const k = Math.max(1, lo);
    const span = cum[k] - cum[k - 1];
    const frac = span > 0 ? (target - cum[k - 1]) / span : 0;
    const u = (k - 1 + frac) / SHAPE_STEPS;
    times.push(Math.round(u * finishS * 1000));
  }
  times[times.length - 1] = Math.round(finishS * 1000);
  return times;
}

/** v1 envelope + per-horse sectionTimesMs under physics version 2. */
export async function deriveTelemetryV2(
  serverSeed,
  clientSeed,
  nonce,
  order,
  raceDurationMs,
  distanceMeters,
  archetypes,
) {
  const envelope = await deriveTelemetry(
    serverSeed, clientSeed, nonce, order, raceDurationMs, distanceMeters,
  );
  const v2gen = new StreamGenerator(
    serverSeed, `${clientSeed}:${TELEMETRY_DOMAIN_V2}`, nonce,
  );
  const horses = [];
  for (let i = 0; i < envelope.horses.length; i++) {
    const sectionSeed = await v2gen.generateFloat();
    const flavoured = flavouredHorse(
      envelope.horses[i], archetypes[i], distanceMeters, sectionSeed,
    );
    horses.push({
      ...envelope.horses[i],
      sectionSeed,
      sectionTimesMs: sectionTimesMs(flavoured),
    });
  }
  return { ...envelope, version: PHYSICS_VERSION_V2, horses };
}
